<template>
    <div 
        class="filter-tabs" 
        @focusin="onFocusin" 
        @focusout="onFocusout"
    >
        <button 
            v-for="(item) in filterItems" 
            :id="getId(item.id)" 
            :ref="getId(item.id)" 
            :key="item.id"
            :data-keyleft="getLeftBtnId(item.id)"
            :data-keyright="getRightBtnId(item.id)"
            class="filter-tabs__item" 
            :class="{'filter-tabs__item_selected':isSelected(item.id)}" 
            @click="setFilter(item.id)"
            @mouseenter="resetUrl"
            @focus="resetUrl"
        >
            {{ item.name }}
        </button>
            
    </div>
</template>

<script lang="ts" setup>
    import { Strings } from "@/locales/types/enums";
    import { useI18n } from "vue-i18n";
    import { useGuideStore } from "@/stores/Guide";
    import { FilterTabsType } from "@/stores/Guide/types";
    import {  type IFilterTabsExposed, type OnKeyUpDown } from "./types";
    import { useUserInfoStore } from "@/stores/UserInfo";

    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);

    const GuideStoreModule = useGuideStore();
    const {
        setFilterParams
    } = GuideStoreModule;
    const {
        filterParams
    } = storeToRefs(GuideStoreModule)

    const { focusToHeader } = useFocus();

    const { $navigator } = useNuxtApp();
    const { t } = useI18n();
    const keyDownListenerBtnFilter = inject<OnKeyUpDown>('keyDownListenerBtnFilter');
    const resetUrl = inject<() => void>('resetUrl');
    const isRegKeyListeners: Ref<boolean> = ref(false);

    const filterItems = computed((): Array<{ name: string; id: FilterTabsType; isEnable: boolean }> => {
        return [
            {
                name: t(Strings.GUIDE_FILTER_TABS_ALL).toString(),
                id: FilterTabsType.ALL,
                isEnable: true
            },
            {
                name: t(Strings.GUIDE_FILTER_TABS_SUBSCRIBED).toString(),
                id: FilterTabsType.SUBSCRIBED,
                isEnable: !isGuest.value
            },
            {
                name: t(Strings.GUIDE_FILTER_TABS_FAVOURITES).toString(),
                id: FilterTabsType.FAVOURITES,
                isEnable: !isGuest.value
            },
            {
                name: t(Strings.GUIDE_FILTER_TABS_OUT_OF_HOME).toString(),
                id: FilterTabsType.OUT_OF_HOME,
                isEnable: !isGuest.value
            }
        ].filter(item => item.isEnable);
    });
    

    const getLeftBtnId = (type: FilterTabsType): string | null => {
        switch (type) {
            case FilterTabsType.ALL:
                return getId(FilterTabsType.OUT_OF_HOME);

            case FilterTabsType.SUBSCRIBED:
                return getId(FilterTabsType.ALL);

            case FilterTabsType.FAVOURITES:
                return getId(FilterTabsType.SUBSCRIBED);

            case FilterTabsType.OUT_OF_HOME:
                return getId(FilterTabsType.FAVOURITES);
        }
    };
    const getRightBtnId = (type: FilterTabsType): string | null => {
        switch (type) {
            case FilterTabsType.ALL:
                return getId(FilterTabsType.SUBSCRIBED);

            case FilterTabsType.SUBSCRIBED:
                return getId(FilterTabsType.FAVOURITES);

            case FilterTabsType.FAVOURITES:
                return getId(FilterTabsType.OUT_OF_HOME);

            case FilterTabsType.OUT_OF_HOME:
                return getId(FilterTabsType.ALL);
        }
    };
    const isSelected = (id: number): boolean => filterParams.value === id;

    const getId = (id: number): string => {
        return `btnFilter_${id}`;
    };

    const setFilter = (id: FilterTabsType): void => {
        if(resetUrl){
            resetUrl();
        }
        setFilterParams(id);
    };
    const registerKeyListeners = (): void => {
        if (isRegKeyListeners.value) {
            return;
        }
        
        $navigator.on("keypress:back", focusToHeader);
        $navigator.on("keypress:up", focusToHeader);
        if (keyDownListenerBtnFilter) {
            $navigator.on("keypress:down", keyDownListenerBtnFilter);
        }
        isRegKeyListeners.value = true;
    };
    const unregisterKeyListeners = (): void => {
        if (!isRegKeyListeners.value) {
            return;
        }

        $navigator.off("keypress:back", focusToHeader);
        $navigator.off("keypress:up", focusToHeader);
        if (keyDownListenerBtnFilter) {
            $navigator.off("keypress:down", keyDownListenerBtnFilter);
        }
        isRegKeyListeners.value = false;
        
    };
    const onFocusin = (): void => {
        registerKeyListeners();
    };
    const onFocusout = (): void => {
        unregisterKeyListeners();
    };
    const focusDefault = (): void => {
        const btn: HTMLElement | null = document.getElementById(getId(filterParams.value));
        btn?.focus();
    };

    onBeforeUnmount(() => {
        unregisterKeyListeners();
    });

    defineExpose<IFilterTabsExposed>({
        focusDefault
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>